// Lib
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  Fragment,
} from "react";
import { DateTime } from "luxon";
import { RangeValue } from "@react-types/shared";
import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  toCalendarDate,
  today,
  ZonedDateTime,
} from "@internationalized/date";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Combobox, Transition } from "@headlessui/react";
import _ from "lodash";
import { useAuthStore } from "../../store/firebase-auth";
// Local
import DateRangePicker from "../../components/DateRangePicker";
import { logger } from "../../logging";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { useToastMessageStore } from "../../store/toast-messages";
import currencyFormatter from "../../currency";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import { DbRead, DbWrite } from "../../database";
import {
  ZiftDepositReconciliation,
  ZiftTransaction,
  StiltPaymentsByDate,
} from "../../database";
import { ExistingStiltPayment } from "../../models/stilt-payment";
import { convertObjectToFSTimestamp } from "../../utils/convertObjectToFSTimestamp";
import { isPlainObject } from "lodash";
import { convertFSTimestampToLuxonDT } from "../../utils";
import { BankDepositStatus } from "../../models/bank-deposit";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import BankDepositConfirmationDialog from "../../components/BankDepositConfirmationDialog";
import { ExistingGLAccount } from "../../models/gl-account";
import { StyledTooltip } from "../../components/StyledTooltip";

interface Props {
  siteKey: string;
  quickbooksEnabled: boolean;
}

const renderDate = (value: any): string => {
  if (!value || value === "Unknown") return "Unknown";

  try {
    let parsableValue = value;

    // Convert FS Timestamp to ISO date
    if (isPlainObject(value)) {
      parsableValue = convertObjectToFSTimestamp(value);
      return DateTime.fromJSDate(parsableValue.toDate()).toFormat("LL/dd/yy");
    }

    // Try to parse as ISO date first
    const date = DateTime.fromISO(value);
    if (date.isValid) {
      return date.toFormat("LL/dd/yy");
    }

    // If ISO parsing fails, try other common formats
    if (
      value.includes("GMT") ||
      value.includes("Z") ||
      !isNaN(Date.parse(value))
    ) {
      return DateTime.fromJSDate(new Date(value)).toFormat("LL/dd/yy");
    }

    // Return the original string if we couldn't parse it
    return value;
  } catch (e) {
    console.error("Date parsing error:", e);
    return value;
  }
};

const renderDateFromFSTimestamp = (value: any): string => {
  return convertFSTimestampToLuxonDT(value).toFormat("LL/dd/yy");
};

const renderCurrency = (value: number): string => {
  return currencyFormatter(value, "USD");
};

interface DepositComboboxProps {
  deposits: ZiftDepositReconciliation[];
  selectedDeposit: ZiftDepositReconciliation | null;
  onDepositChange: (deposit: ZiftDepositReconciliation | null) => void;
}

function DepositCombobox({
  deposits,
  selectedDeposit,
  onDepositChange,
}: DepositComboboxProps) {
  const [query, setQuery] = useState<string>("");
  const buttonRef = useRef<HTMLButtonElement>(null);

  const filteredDeposits =
    query === ""
      ? deposits
      : deposits.filter((deposit) => {
          const depositId = deposit.depositId?.toLowerCase() || "";
          const accountName = deposit.accountName?.toLowerCase() || "";
          const date = DateTime.fromISO(deposit.remittanceDate)
            .toFormat("LL/dd/yyyy")
            .toLowerCase();
          const amount = deposit.totalAmount.toString();
          const searchQuery = query.toLowerCase();

          return (
            depositId.includes(searchQuery) ||
            accountName.includes(searchQuery) ||
            date.includes(searchQuery) ||
            amount.includes(searchQuery)
          );
        });

  const formatDepositOption = (deposit: ZiftDepositReconciliation) => {
    return `${deposit.depositId} • ${DateTime.fromISO(deposit.remittanceDate).toFormat("LL/dd/yyyy")} • ${currencyFormatter(deposit.totalAmount, "USD")} • ${deposit.transactionCount} Transactions`;
  };

  return (
    <div className="w-full max-w-md">
      <Combobox value={selectedDeposit} onChange={onDepositChange}>
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg border border-gray-300 bg-white text-left focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
            <Combobox.Input
              className="w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:outline-none"
              placeholder="Select a deposit..."
              displayValue={(deposit: ZiftDepositReconciliation | null) =>
                deposit ? formatDepositOption(deposit) : ""
              }
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
            />
            <Combobox.Button
              className="absolute inset-y-0 right-0 flex items-center pr-2"
              ref={buttonRef}
            >
              <ChevronDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredDeposits.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  No deposits found.
                </div>
              ) : (
                filteredDeposits.map((deposit) => (
                  <Combobox.Option
                    key={deposit.depositId}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-4 pr-4 ${
                        active
                          ? "bg-primaryOpacity90 text-primary"
                          : "text-gray-900"
                      }`
                    }
                    value={deposit}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                        >
                          {formatDepositOption(deposit)}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 right-3 flex items-center ${
                              active ? "text-white" : "text-primary"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}

export default function CreditCardReconciliationContainer(
  props: Props,
): JSX.Element {
  const siteKey = props.siteKey;
  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  // State
  const [depositsLoading, setDepositsLoading] = useState<boolean>(false);
  const [deposits, setDeposits] = useState<ZiftDepositReconciliation[]>([]);
  const [selectedDeposit, setSelectedDeposit] =
    useState<ZiftDepositReconciliation | null>(null);
  const [stiltPayments, setStiltPayments] = useState<StiltPaymentsByDate[]>([]);
  const [stiltPaymentsLoading, setStiltPaymentsLoading] =
    useState<boolean>(false);
  const [transactionDateRange, setTransactionDateRange] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [range, setRange] = useState<RangeValue<CalendarDate>>({
    start: today(getLocalTimeZone()).subtract({ days: 7 }),
    end: today(getLocalTimeZone()),
  });

  // Fixed cutoff time: 8:30 PM EST
  const CUTOFF_TIME = "20:30";
  const [customDateRange, setCustomDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({ start: null, end: null });
  const [useCustomDateRange, setUseCustomDateRange] = useState<boolean>(false);

  const [isCreatingDeposit, setIsCreatingDeposit] = useState(false);

  // Add GL account state
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  const [glAccounts, setGlAccounts] = useState<ExistingGLAccount[]>([]);
  const [isLoadingGLAccounts, setIsLoadingGLAccounts] = useState(false);

  // Fetch deposits data
  const fetchDeposits = async () => {
    if (!range.start || !range.end) return;

    const fromDate = range.start.toString();
    const toDate = range.end.toString();

    setDepositsLoading(true);

    try {
      // Fetch Zift deposits using the database method
      const deposits = await DbRead.reconciliation.getDepositsForReconciliation(
        siteKey,
        fromDate,
        toDate,
      );
      setDeposits(deposits);

      // Clear selected deposit when new data is fetched
      setSelectedDeposit(null);
      setTransactionDateRange(null);
      setStiltPayments([]);
      setUseCustomDateRange(false);
      setCustomDateRange({ start: null, end: null });
    } catch (error) {
      logger.error("Error fetching deposit data", error);
      addMessage({
        message: `Error fetching deposit data: ${error instanceof Error ? error.message : "Unknown error"}`,
        type: "error",
        dialog: false,
        id: "reconciliation-deposits-error",
      });
    } finally {
      setDepositsLoading(false);
    }
  };

  // Apply time cutoff to date
  const applyTimeCutoffToDate = (
    date: string,
    isStartDate: boolean,
  ): string => {
    try {
      const baseDate = DateTime.fromISO(date);
      if (!baseDate.isValid) return date;

      // Split the cutoff time into hours and minutes
      const [hours, minutes] = CUTOFF_TIME.split(":").map(Number);

      // For both start and end dates, we use the cutoff time (8:30 PM EST)
      return baseDate.set({ hour: hours, minute: minutes }).toISO() || date;
    } catch (e) {
      console.error("Error applying time cutoff:", e);
      return date;
    }
  };

  // Calculate the payment date range based on transaction dates
  function calculatePaymentDateRangeFromTransactions(
    transactions: ZiftTransaction[],
  ): { start: string; end: string } {
    // Extract all transaction dates and convert to DateTime objects
    const dates = transactions.map((t) => DateTime.fromISO(t.activityDate));

    // Find the earliest and latest dates
    const earliestDate = DateTime.min(...dates);
    const latestDate = DateTime.max(...dates);

    // Start date is the day BEFORE the earliest transaction date
    const startDate = earliestDate.minus({ days: 1 }).toISODate() || "";

    // End date is the latest transaction date
    const endDate = latestDate.toISODate() || "";

    return { start: startDate, end: endDate };
  }

  // Fetch payments based on transaction date range with cutoff time
  const fetchPayments = useCallback(
    async (startDateParam?: string, endDateParam?: string) => {
      let startDate: string;
      let endDate: string;

      // Use passed parameters if available, otherwise use state
      if (startDateParam && endDateParam) {
        startDate = applyTimeCutoffToDate(startDateParam, true);
        endDate = applyTimeCutoffToDate(endDateParam, false);
      } else if (
        useCustomDateRange &&
        customDateRange.start &&
        customDateRange.end
      ) {
        // Use custom date range with cutoff time
        console.log(
          "Using custom date range",
          customDateRange.start,
          customDateRange.end,
        );
        startDate = applyTimeCutoffToDate(customDateRange.start, true);
        endDate = applyTimeCutoffToDate(customDateRange.end, false);
      } else if (transactionDateRange) {
        // Use transaction date range with cutoff time
        console.log(
          "Using transaction date range",
          transactionDateRange.start,
          transactionDateRange.end,
        );
        startDate = applyTimeCutoffToDate(transactionDateRange.start, true);
        endDate = applyTimeCutoffToDate(transactionDateRange.end, false);
      } else {
        return; // No valid date range
      }

      setStiltPaymentsLoading(true);

      try {
        // Fetch Stilt payments using the database method
        console.log("Fetching payments for", startDate, endDate);
        const payments =
          await DbRead.reconciliation.getPaymentsForReconciliation(
            siteKey,
            startDate,
            endDate,
          );
        setStiltPayments(payments);
      } catch (error) {
        logger.error("Error fetching payment data", error);
        addMessage({
          message: `Error fetching payment data: ${error instanceof Error ? error.message : "Unknown error"}`,
          type: "error",
          dialog: false,
          id: "reconciliation-payments-error",
        });
      } finally {
        setStiltPaymentsLoading(false);
      }
    },
    [siteKey, useCustomDateRange, customDateRange, transactionDateRange],
  );

  // When a deposit is selected, determine the transaction date range and fetch payments
  useEffect(() => {
    if (!selectedDeposit) return;

    // Calculate payment date range based on transaction dates
    const calculatedDateRange = calculatePaymentDateRangeFromTransactions(
      selectedDeposit.transactions,
    );

    // First update the state (this is asynchronous)
    setTransactionDateRange(calculatedDateRange);
    setCustomDateRange(calculatedDateRange);

    // Then fetch payments with the calculated date range directly
    // This ensures we use the correct dates even if state hasn't updated yet
    fetchPayments(calculatedDateRange.start, calculatedDateRange.end);
  }, [selectedDeposit]);

  // Handle date range changes
  function handleDateRangeChange(
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ) {
    logger.debug(
      `Invoked handleDateRangeChange for range: ${value.start?.month}-${value.start?.day} to ${value.end?.month}-${value.end?.day}`,
    );
    try {
      if (value.start && value.end) {
        const convertedStart = toCalendarDate(value.start);
        const convertedEnd = toCalendarDate(value.end);
        const converted = { start: convertedStart, end: convertedEnd };
        setRange(converted);
      }
    } catch (e) {
      if (e instanceof Error) {
        logger.error("Error in handleDateRangeChange", e);
      }
    }
  }

  // Get all payments for the selected deposit's transaction dates
  const getAllPaymentsForDepositTransactions = (): ExistingStiltPayment[] => {
    if (!selectedDeposit || stiltPayments.length === 0) return [];

    // Flatten all payments from the StiltPaymentsByDate array
    let allPayments: ExistingStiltPayment[] = [];
    stiltPayments.forEach((paymentByDate) => {
      if (paymentByDate.payments && Array.isArray(paymentByDate.payments)) {
        allPayments = [...allPayments, ...paymentByDate.payments];
      }
    });

    return allPayments;
  };

  // Calculate correct Zift total accounting for transaction types
  const calculateZiftTotalWithCreditHandling = (): number => {
    if (!selectedDeposit || !selectedDeposit.transactions) return 0;

    return selectedDeposit.transactions.reduce((total, transaction) => {
      // If transaction type is 'credit', subtract it instead of adding
      if (transaction.type.toLowerCase() === "credit") {
        return total - transaction.netAmount;
      } else {
        return total + transaction.netAmount;
      }
    }, 0);
  };

  // Sort and process transactions and payments
  const processTransactionsAndPayments = () => {
    if (!selectedDeposit) {
      return {
        sortedTransactions: [],
        sortedPayments: [],
        matchingIds: {},
        unmatchedTransactions: [],
        unmatchedPayments: [],
        paymentsWithoutRef: [],
        alreadyDepositedPayments: [],
      };
    }

    // Get Zift transactions from the selected deposit
    const ziftTransactions = [...selectedDeposit.transactions].sort((a, b) =>
      a.transactionId.localeCompare(b.transactionId),
    );

    // Get all Stilt payments for the transaction date range
    const allPayments = getAllPaymentsForDepositTransactions();

    // Track already deposited payments
    const alreadyDepositedPayments: string[] = [];

    // Separate payments with and without pnRef, and filter out already deposited payments
    const paymentsWithRef: ExistingStiltPayment[] = [];
    const paymentsWithoutRef: ExistingStiltPayment[] = [];

    allPayments.forEach((payment) => {
      // Check if payment is already in a deposit
      if (payment.bankDepositID !== undefined) {
        alreadyDepositedPayments.push(payment.id);
        return; // Skip this payment for matching purposes
      }

      if (payment.customData?.pnRef) {
        paymentsWithRef.push(payment);
      } else {
        paymentsWithoutRef.push(payment);
      }
    });

    // Sort payments with pnRef
    const sortedPaymentsWithRef = [...paymentsWithRef].sort((a, b) => {
      const aRef = a.customData?.pnRef || "";
      const bRef = b.customData?.pnRef || "";
      return aRef.localeCompare(bRef);
    });

    // Create a map of matching transaction IDs to payment IDs
    const matchingIds: Record<string, string> = {};
    const unmatchedTransactions: string[] = [];
    const unmatchedPayments: string[] = [];

    // Mark matching transactions and payments
    ziftTransactions.forEach((transaction) => {
      const matchingPayment = sortedPaymentsWithRef.find(
        (payment) => payment.customData?.pnRef === transaction.transactionId,
      );

      if (matchingPayment) {
        matchingIds[transaction.transactionId] = matchingPayment.id;
      } else {
        unmatchedTransactions.push(transaction.transactionId);
      }
    });

    // Find unmatched payments
    sortedPaymentsWithRef.forEach((payment) => {
      const pnRef = payment.customData?.pnRef || "";
      const matchingTransaction = ziftTransactions.find(
        (transaction) => transaction.transactionId === pnRef,
      );

      if (!matchingTransaction) {
        unmatchedPayments.push(payment.id);
      }
    });

    return {
      sortedTransactions: ziftTransactions,
      sortedPayments: [...sortedPaymentsWithRef, ...paymentsWithoutRef],
      matchingIds,
      unmatchedTransactions,
      unmatchedPayments,
      paymentsWithoutRef,
      alreadyDepositedPayments,
    };
  };

  // Calculate totals and discrepancy
  const allPayments = getAllPaymentsForDepositTransactions();
  const stiltPaymentsTotal = allPayments.reduce(
    (sum, payment) => sum + payment.amount,
    0,
  );
  const ziftTransactionsTotal = calculateZiftTotalWithCreditHandling();
  const discrepancyAmount = ziftTransactionsTotal - stiltPaymentsTotal;
  const hasDiscrepancy = Math.abs(discrepancyAmount) > 0.01; // Account for floating point precision

  // Format a date range for display
  const formatDateRangeWithCutoff = (start: string, end: string) => {
    try {
      const startDate = DateTime.fromISO(start);
      const endDate = DateTime.fromISO(end);
      if (!startDate.isValid || !endDate.isValid) return `${start} to ${end}`;

      return `${startDate.toFormat("LL/dd/yyyy")} 8:30 PM EST to ${endDate.toFormat("LL/dd/yyyy")} 8:30 PM EST`;
    } catch (e) {
      return `${start} to ${end}`;
    }
  };

  // Update the handleDepositChange function
  const handleDepositChange = (deposit: ZiftDepositReconciliation | null) => {
    setSelectedDeposit(deposit);
    // Clear relevant states, but don't automatically trigger fetchPayments here
    // if we want it tied strictly to deposit selection effect or custom range button
    if (!deposit) {
      setTransactionDateRange(null);
      setStiltPayments([]);
      setStiltPaymentsLoading(false);
      setUseCustomDateRange(false);
      setCustomDateRange({ start: null, end: null });
    }
    // Note: fetchPayments is handled by the useEffect hook watching selectedDeposit
  };

  // Fetch GL accounts when component mounts
  useEffect(() => {
    const fetchGLAccounts = async () => {
      try {
        setIsLoadingGLAccounts(true);
        const unsubscribe = DbRead.gLAccounts.subscribeAll({
          siteKey: props.siteKey,
          onChange: (accounts: ExistingGLAccount[]) => {
            setGlAccounts(accounts);
            setIsLoadingGLAccounts(false);
          },
          onError: (error) => {
            logger.error("Error fetching GL accounts:", error);
            addMessage({
              message: `Failed to load GL accounts: ${error instanceof Error ? error.message : "Unknown error"}. You may not be able to create a bank deposit.`,
              type: "error",
              id: "credit-card-recon-gl-accounts-error",
              dialog: false,
            });
            setIsLoadingGLAccounts(false);
          },
        });

        return () => {
          if (unsubscribe) {
            unsubscribe();
          }
        };
      } catch (error) {
        logger.error("Error setting up GL accounts subscription:", error);
        setIsLoadingGLAccounts(false);
      }
    };

    fetchGLAccounts();
  }, [props.siteKey, addMessage]);

  // Function to open the deposit dialog
  const handleOpenDepositDialog = () => {
    if (!selectedDeposit) {
      addMessage({
        message: "No deposit selected",
        type: "error",
        dialog: false,
        id: "credit-card-recon-no-deposit-selected",
      });
      return;
    }

    // Verify we have processed data
    const processedData = processTransactionsAndPayments();
    const { sortedPayments: processedPayments, matchingIds } = processedData;

    if (!processedPayments.length) {
      addMessage({
        message: "No payments available for this deposit",
        type: "error",
        dialog: false,
        id: "credit-card-recon-no-payments-available",
      });
      return;
    }

    // Check if any matched payments aren't synced to QuickBooks when QuickBooks is enabled
    if (props.quickbooksEnabled) {
      // Get all matched payments that will be included in the deposit
      const matchedPayments = processedPayments.filter((payment) => {
        const pnRef = payment.customData?.pnRef || "";
        return pnRef && matchingIds[pnRef] === payment.id;
      });

      // Filter out any that don't have an accounting reference ID
      const unsyncedPayments = matchedPayments.filter(
        (payment) => !payment.customData?.accountingRefID,
      );

      if (unsyncedPayments.length > 0) {
        addMessage({
          message: `Cannot create bank deposit: ${unsyncedPayments.length} payment(s) are not synced to QuickBooks. Please sync these payments first.`,
          type: "error",
          dialog: false,
          id: "credit-card-recon-unsynced-payments",
        });
        return;
      }
    }

    setShowDepositDialog(true);
  };

  // Function to create a bank deposit from the selected deposit and matching payments
  const handleCreateBankDeposit = async (
    selectedGLAccount: ExistingGLAccount,
    depositDate: string,
    memo: string,
    depositAmount: number,
  ) => {
    if (!selectedDeposit) {
      addMessage({
        message: "No deposit selected",
        type: "error",
        dialog: false,
        id: "credit-card-recon-no-deposit-selected",
      });
      return;
    }

    try {
      // First get the processed data to access matchingIds
      const processedData = processTransactionsAndPayments();
      const {
        matchingIds,
        sortedPayments: processedPayments,
        alreadyDepositedPayments,
      } = processedData;

      if (!processedPayments.length) {
        addMessage({
          message: "No payments available for this deposit",
          type: "error",
          dialog: false,
          id: "credit-card-recon-no-payments-available",
        });
        return;
      }

      // Now we can safely proceed
      setIsCreatingDeposit(true);

      // Get all matched payment IDs (excluding already deposited payments)
      const matchedPaymentIds = processedPayments
        .filter((payment) => {
          const pnRef = payment.customData?.pnRef || "";
          // Make sure payment is matched and not already in a deposit
          return (
            pnRef &&
            matchingIds[pnRef] === payment.id &&
            !alreadyDepositedPayments.includes(payment.id)
          );
        })
        .map((payment) => payment.id);

      if (matchedPaymentIds.length === 0) {
        addMessage({
          message: "No matched payments available for deposit",
          type: "error",
          dialog: false,
          id: "credit-card-recon-no-matched-payments-available",
        });
        setIsCreatingDeposit(false);
        setShowDepositDialog(false);
        return;
      }

      // Prepare bank deposit request data
      const bankDepositData = {
        siteKey: props.siteKey,
        glAccountID: selectedGLAccount.id,
        totalAmount: depositAmount,
        paymentIDs: matchedPaymentIds,
        memo: memo || `Zift Deposit ID: ${selectedDeposit.depositId}`,
        timestampDepositedISO: depositDate,
        status: "deposited" as BankDepositStatus,
        customData: {},
        createdBy: firebaseUser?.uid || "",
      };

      await DbWrite.bankDeposits.create({ bankDeposit: bankDepositData });

      // Show success message
      addMessage({
        message: `Bank deposit created successfully for ${matchedPaymentIds.length} payments totaling ${currencyFormatter(depositAmount, "USD")}`,
        type: "success",
        dialog: false,
        id: "reconciliation-bank-deposit-created",
      });

      // Optional: Refresh data after deposit creation
      if (selectedDeposit) {
        await fetchPayments();
      }
    } catch (error) {
      console.error("Error creating bank deposit:", error);
      addMessage({
        message: `Error creating bank deposit: ${error instanceof Error ? error.message : "Unknown error"}`,
        type: "error",
        dialog: false,
        id: "credit-card-recon-deposit-error",
      });
    } finally {
      setIsCreatingDeposit(false);
      setShowDepositDialog(false);
    }
  };

  // Handle the Apply Custom Range button click
  const handleApplyCustomRange = (event: React.MouseEvent) => {
    event.preventDefault();
    if (customDateRange.start && customDateRange.end) {
      fetchPayments(customDateRange.start, customDateRange.end);
    }
  };

  // Create date range picker with Fetch Data button
  const dateRangePicker = (
    <div className="mb-6 rounded-lg bg-white p-4 shadow-md">
      <div className="flex w-full flex-row gap-4">
        <div>
          <DateRangePicker
            value={range as any}
            onChange={handleDateRangeChange}
          />
        </div>

        <BaseButtonPrimary
          onClick={fetchDeposits}
          disabled={depositsLoading}
          className="h-10"
          isBusy={depositsLoading}
        >
          {depositsLoading ? "Loading..." : "Fetch Deposits"}
        </BaseButtonPrimary>

        <BaseButtonSecondary
          onClick={handleOpenDepositDialog}
          disabled={
            !selectedDeposit ||
            depositsLoading ||
            stiltPaymentsLoading ||
            isCreatingDeposit
          }
          isBusy={isCreatingDeposit}
        >
          {isCreatingDeposit ? "Creating..." : "Create Bank Deposit"}
        </BaseButtonSecondary>

        <div className="flex flex-grow">
          {deposits.length > 0 ? (
            <DepositCombobox
              deposits={deposits}
              selectedDeposit={selectedDeposit}
              onDepositChange={handleDepositChange}
            />
          ) : null}
        </div>
      </div>
    </div>
  );

  // Create custom date range selector for Reconciliation Summary panel
  const customDateRangeSelector = (
    <div className="mt-4 border-t border-gray-200 pt-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium">Payment Date Range Override</span>
        <div className="relative mr-2 inline-block w-10 select-none align-middle">
          <input
            type="checkbox"
            id="use-custom-dates"
            checked={useCustomDateRange}
            onChange={(e) => setUseCustomDateRange(e.target.checked)}
            className="absolute right-4 block h-6 w-6 cursor-pointer appearance-none rounded-full border-4 bg-white outline-none duration-200 ease-in checked:right-0 checked:bg-blue-500 focus:outline-none"
          />
          <label
            htmlFor="use-custom-dates"
            className="block h-6 cursor-pointer overflow-hidden rounded-full bg-gray-300"
          ></label>
        </div>
      </div>

      {useCustomDateRange && (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="flex-1">
              <label
                htmlFor="custom-start-date"
                className="block text-xs font-medium text-gray-700"
              >
                Start Date
              </label>
              <input
                type="date"
                id="custom-start-date"
                value={customDateRange.start || ""}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    start: e.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="custom-end-date"
                className="block text-xs font-medium text-gray-700"
              >
                End Date
              </label>
              <input
                type="date"
                id="custom-end-date"
                value={customDateRange.end || ""}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    end: e.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>
          <BaseButtonPrimary
            onClick={handleApplyCustomRange}
            isBusy={stiltPaymentsLoading}
            disabled={!customDateRange.start || !customDateRange.end}
            className="mt-1 w-full py-1 text-sm"
          >
            Apply Custom Range
          </BaseButtonPrimary>
        </div>
      )}

      <div className="mt-2 text-xs text-gray-500">
        <div>Using 8:30 PM EST as Zift's deposit cutoff time</div>
        <div>
          {useCustomDateRange
            ? `Custom date range: ${formatDateRangeWithCutoff(customDateRange.start || "", customDateRange.end || "")}`
            : transactionDateRange
              ? `Default payment range: ${formatDateRangeWithCutoff(transactionDateRange.start, transactionDateRange.end)}`
              : "Select a deposit to see payment details"}
        </div>
      </div>
    </div>
  );

  // Process transactions and payments for display
  const {
    sortedTransactions,
    sortedPayments,
    matchingIds,
    unmatchedTransactions,
    unmatchedPayments,
    paymentsWithoutRef,
    alreadyDepositedPayments,
  } = processTransactionsAndPayments();

  // Render deposit details component with side-by-side comparison
  const depositDetailsComponent = selectedDeposit ? (
    <div>
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded bg-gray-50 p-3">
          <h3 className="font-medium text-gray-700">Deposit Information</h3>
          <div className="mt-2 space-y-2 text-sm">
            <div>
              <span className="font-medium">Date:</span>{" "}
              {DateTime.fromISO(selectedDeposit.remittanceDate).toFormat(
                "LL/dd/yyyy",
              )}
            </div>
            <div>
              <span className="font-medium">Deposit ID:</span>{" "}
              {selectedDeposit.depositId}
            </div>
            <div>
              <span className="font-medium">Account:</span>{" "}
              {selectedDeposit.accountName}
            </div>
            <div>
              <span className="font-medium">Amount:</span>{" "}
              {currencyFormatter(selectedDeposit.totalAmount, "USD")}
            </div>
            <div>
              <span className="font-medium">Transaction Count:</span>{" "}
              {selectedDeposit.transactionCount}
            </div>
            <div>
              <span className="font-medium">Transaction Dates:</span>{" "}
              {selectedDeposit.transactions
                .map((t) =>
                  DateTime.fromISO(t.activityDate).toFormat("LL/dd/yyyy"),
                )
                .filter((v, i, a) => a.indexOf(v) === i)
                .join(", ")}
            </div>
          </div>
        </div>

        <div
          className={`rounded p-3 ${hasDiscrepancy ? "bg-red-50" : "bg-green-50"}`}
        >
          <h3 className="font-medium text-gray-700">Reconciliation Summary</h3>
          <div className="mt-2 space-y-2 text-sm">
            <div>
              <span className="font-medium">Zift Transactions Total:</span>{" "}
              {currencyFormatter(ziftTransactionsTotal, "USD")}
            </div>
            <div>
              <span className="font-medium">Stilt Payments Total:</span>{" "}
              {currencyFormatter(stiltPaymentsTotal, "USD")}
            </div>
            <div
              className={`font-medium ${hasDiscrepancy ? "text-red-600" : "text-green-600"}`}
            >
              {hasDiscrepancy
                ? `Discrepancy: ${currencyFormatter(Math.abs(discrepancyAmount), "USD")} ${discrepancyAmount > 0 ? "(Missing in Stilt)" : "(Extra in Stilt)"}`
                : "Amounts Match"}
            </div>
            <div>
              <span className="font-medium">Matched transactions:</span>{" "}
              {Object.keys(matchingIds).length} of {sortedTransactions.length}
            </div>
            <div>
              <span className="font-medium">
                Payments without transaction ID:
              </span>{" "}
              {paymentsWithoutRef.length}
            </div>
            {customDateRangeSelector}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Left side: Zift Transactions */}
          <div className="flex-1 rounded-lg bg-white p-4 shadow-md">
            <h3 className="text-md mb-4 font-medium">
              Zift Transactions ({sortedTransactions.length})
            </h3>
            {sortedTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Transaction ID
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Card Holder
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {sortedTransactions.map((transaction) => {
                      const isMatched = matchingIds[transaction.transactionId];
                      const isUnmatched = unmatchedTransactions.includes(
                        transaction.transactionId,
                      );
                      let rowClass = "";

                      if (isMatched) {
                        rowClass = "bg-green-50";
                      } else if (isUnmatched) {
                        rowClass = "bg-yellow-50";
                      }

                      // For credit type transactions, show amount in a different color
                      const isCredit =
                        transaction.type.toLowerCase() === "credit";

                      return (
                        <tr
                          key={transaction.transactionId}
                          className={rowClass}
                        >
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {renderDate(transaction.activityDate)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {transaction.transactionId}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {transaction.type}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {transaction.holderName}
                          </td>
                          <td
                            className={`whitespace-nowrap px-3 py-2 text-sm ${isCredit ? "text-red-500" : "text-gray-500"}`}
                          >
                            {renderCurrency(transaction.netAmount)}
                            {isCredit && " (Credit)"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-4 text-center text-gray-500">
                No transactions found
              </div>
            )}
          </div>

          {/* Right side: Stilt Payments */}
          <div className="flex-1 rounded-lg bg-white p-4 shadow-md">
            <h3 className="text-md mb-4 font-medium">
              Stilt Payments ({sortedPayments.length})
            </h3>
            {stiltPaymentsLoading ? (
              <div className="flex h-64 items-center justify-center">
                <LoadingClipboardAnimation />
              </div>
            ) : sortedPayments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Transaction ID
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Payment Method
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Amount
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Status
                      </th>
                      {props.quickbooksEnabled && (
                        <th
                          scope="col"
                          className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                        >
                          QB Sync
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {sortedPayments.map((payment) => {
                      const pnRef = payment.customData?.pnRef || "";
                      const isMatched =
                        pnRef && matchingIds[pnRef] === payment.id;
                      const isUnmatched =
                        pnRef && unmatchedPayments.includes(payment.id);
                      const isWithoutRef = !pnRef;
                      const isNegative = payment.amount < 0;
                      const isAlreadyDeposited =
                        payment.bankDepositID !== undefined;

                      let rowClass = "";
                      if (isAlreadyDeposited) {
                        rowClass = "bg-gray-100";
                      } else if (isMatched) {
                        rowClass = "bg-green-50";
                      } else if (isUnmatched) {
                        rowClass = "bg-yellow-50";
                      } else if (isWithoutRef) {
                        rowClass = "bg-purple-50";
                      }

                      return (
                        <tr key={payment.id} className={rowClass}>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {renderDateFromFSTimestamp(
                              payment.timestampPaymentMade,
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {payment.customData?.pnRef || "N/A"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {payment.paymentMethod}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {payment.nameOnCard ||
                              payment.customData?.customerName ||
                              "N/A"}
                          </td>
                          <td
                            className={`whitespace-nowrap px-3 py-2 text-sm ${isNegative ? "text-red-500" : "text-gray-500"}`}
                          >
                            {renderCurrency(payment.amount)}
                            {isNegative && " (Refund)"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                            {isAlreadyDeposited && (
                              <span
                                className="rounded-full bg-gray-200 px-1.5 py-0.5 text-xs"
                                title="This payment has already been deposited"
                              >
                                Deposited
                              </span>
                            )}
                          </td>
                          {props.quickbooksEnabled && (
                            <td className="whitespace-nowrap px-3 py-1 text-center">
                              {payment.customData?.accountingRefID ? (
                                <CheckIcon
                                  className="mx-auto h-5 w-5 text-green-500"
                                  title="Synced with QuickBooks"
                                />
                              ) : (
                                <div className="flex justify-center">
                                  <StyledTooltip
                                    title={
                                      payment.accountingSync?.statusMessage ??
                                      "Not synced with QuickBooks"
                                    }
                                  >
                                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                                  </StyledTooltip>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-4 text-center text-gray-500">
                {transactionDateRange
                  ? "No payments found for this date range"
                  : "Select a deposit to view payments"}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded bg-gray-50 p-3">
        <h3 className="mb-2 text-sm font-medium">Legend</h3>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
          <div className="flex items-center">
            <div className="mr-2 h-4 w-4 border border-green-200 bg-green-50"></div>
            <span className="text-xs">Matching records</span>
          </div>
          <div className="flex items-center">
            <div className="mr-2 h-4 w-4 border border-yellow-200 bg-yellow-50"></div>
            <span className="text-xs">No matching record found</span>
          </div>
          <div className="flex items-center">
            <div className="mr-2 h-4 w-4 border border-purple-200 bg-purple-50"></div>
            <span className="text-xs">Payment without transaction ID</span>
          </div>
          <div className="flex items-center">
            <div className="mr-2 h-4 w-4 border border-red-200 bg-white text-center text-xs text-red-500">
              $
            </div>
            <span className="text-xs">Credit/Refund amount</span>
          </div>
          <div className="flex items-center">
            <div className="mr-2 h-4 w-4 border border-gray-300 bg-gray-100"></div>
            <span className="text-xs">Already deposited</span>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex h-64 items-center justify-center rounded-lg bg-white p-4 text-gray-500 shadow-md">
      {depositsLoading ? (
        <LoadingClipboardAnimation />
      ) : deposits.length > 0 ? (
        "Select a deposit to view details"
      ) : (
        "Click 'Fetch Deposits' to load deposits for the selected date range"
      )}
    </div>
  );

  return (
    <div className="isolate pb-14">
      {dateRangePicker}
      {depositDetailsComponent}
      {/* Bank Deposit Confirmation Dialog */}
      <BankDepositConfirmationDialog
        isOpen={showDepositDialog}
        quickbooksEnabled={props.quickbooksEnabled}
        onClose={() => setShowDepositDialog(false)}
        onConfirm={handleCreateBankDeposit}
        totalAmount={stiltPaymentsTotal}
        paymentCount={Object.keys(matchingIds).length}
        depositType="credit-card"
        isLoading={isCreatingDeposit}
        glAccounts={glAccounts}
        siteKey={props.siteKey}
        firebaseUser={firebaseUser!}
        currency="USD"
      />
    </div>
  );
}
